import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Activity, TrendingUp, CalendarDays, Gauge, TrendingUp as GrowthIcon, DollarSign, Stethoscope, ClipboardList } from 'lucide-react';
import '../../../styles/clinic-dashboard.css';

import LandingCard from './components/LandingCard';
import DateFilter from './components/DateFilter';
import { useClinicDashboard } from './context/ClinicDashboardContext';
import PerformanceOverview from './views/PerformanceOverview';
import FinancialHealth from './views/FinancialHealth';
import DailyOperations from './views/DailyOperations';
import CapacityUtilization from './views/CapacityUtilization';
import GrowthTrends from './views/GrowthTrends';
import ClinicalActivity from './views/ClinicalActivity';
import AppointmentClinicalFlow from './views/AppointmentClinicalFlow';
import InpatientClinicalManagement from './views/InpatientClinicalManagement';
import OutpatientCare from './views/OutpatientCare';
import VeterinarianWorkload from './views/VeterinarianWorkload';
import VeterinarianProductivity from './views/VeterinarianProductivity';

const VIEWS = {
    landing: 'landing',
    performance: 'performance',
    financial: 'financial',
    daily: 'daily',
    capacity: 'capacity',
    growth: 'growth',
    clinical: 'clinical',
    appointment: 'appointment',
    inpatient: 'inpatient',
    outpatient: 'outpatient',
    workload: 'workload',
    productivity: 'productivity',
};

const VIEW_PARAM_MAP = {
    'performance': VIEWS.performance,
    'financial': VIEWS.financial,
    'daily': VIEWS.daily,
    'capacity': VIEWS.capacity,
    'growth': VIEWS.growth,
    'clinical': VIEWS.clinical,
    'appointment': VIEWS.appointment,
    'inpatient': VIEWS.inpatient,
    'outpatient': VIEWS.outpatient,
    'workload': VIEWS.workload,
    'productivity': VIEWS.productivity,
};

const PARAM_VIEW_MAP = {
    [VIEWS.performance]: 'performance',
    [VIEWS.financial]: 'financial',
    [VIEWS.daily]: 'daily',
    [VIEWS.capacity]: 'capacity',
    [VIEWS.growth]: 'growth',
    [VIEWS.clinical]: 'clinical',
    [VIEWS.appointment]: 'appointment',
    [VIEWS.inpatient]: 'inpatient',
    [VIEWS.outpatient]: 'outpatient',
    [VIEWS.workload]: 'workload',
    [VIEWS.productivity]: 'productivity',
};

const ClinicDashboard = ({ clinicData, view }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { clinicId } = useParams();

    const currentView = view ? (VIEW_PARAM_MAP[view] || VIEWS.landing) : VIEWS.landing;

    const { sharedMonthYear, setSharedMonthYear } = useClinicDashboard();
    const [summaryFilters, setSummaryFilters] = React.useState({
        month: sharedMonthYear.month,
        year: sharedMonthYear.year,
        start_date: '',
        end_date: ''
    });

    // Sync local filter changes to shared context
    React.useEffect(() => {
        setSharedMonthYear({ month: summaryFilters.month, year: summaryFilters.year });
    }, [summaryFilters.month, summaryFilters.year]);

    const [summary, setSummary] = React.useState({
        performance_revenue: 0,
        financial_revenue: 0,
        growth_new_clients: 0,
        vet_productivity_avg: 0,
        clinical_activity_appts: 0,
        inpatient_current: 0,
        outpatient_visits: 0,
        appointment_completed: 0,
        daily_appointments: 0,
        capacity_utilization: 0,
        vet_workload_active: 0
    });

    const [loadingSummary, setLoadingSummary] = React.useState(false);

    React.useEffect(() => {
        const fetchSummary = async () => {
            if (currentView === VIEWS.landing && clinicId) {
                try {
                    setLoadingSummary(true);
                    const { dashboardAPI } = await import('../../../services/dashboardService');
                    const params = {};
                    if (summaryFilters.year) params.year = summaryFilters.year;
                    if (summaryFilters.month !== undefined) params.month = summaryFilters.month + 1;
                    const response = await dashboardAPI.getDashboardSummary(clinicId, params);
                    if (response.data) {
                        setSummary(response.data);
                    }
                } catch (error) {
                    console.error('Failed to fetch dashboard summary:', error);
                } finally {
                    setLoadingSummary(false);
                }
            }
        };

        fetchSummary();
    }, [clinicId, currentView, summaryFilters]);

    const navigateTo = (viewKey) => {
        const param = PARAM_VIEW_MAP[viewKey];
        if (param) {
            navigate(`/medical/clinic/${clinicId}/dashboard/${param}`);
        } else {
            navigate(`/medical/clinic/${clinicId}/dashboard`);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const currency = clinicData?.clinic?.currency || 'JOD';

    if (currentView === VIEWS.performance) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <PerformanceOverview onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    if (currentView === VIEWS.financial) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <FinancialHealth onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    if (currentView === VIEWS.daily) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <DailyOperations onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    if (currentView === VIEWS.capacity) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <CapacityUtilization onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    if (currentView === VIEWS.growth) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <GrowthTrends onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    if (currentView === VIEWS.clinical) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <ClinicalActivity onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    if (currentView === VIEWS.appointment) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <AppointmentClinicalFlow onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    if (currentView === VIEWS.inpatient) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <InpatientClinicalManagement onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    if (currentView === VIEWS.outpatient) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <OutpatientCare onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    if (currentView === VIEWS.workload) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <VeterinarianWorkload onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    if (currentView === VIEWS.productivity) {
        return (
            <div className="clinic-dashboard clinic-dashboard-view-enter" style={{ overflow: 'hidden' }}>
                <VeterinarianProductivity onBack={() => navigateTo(VIEWS.landing)} currency={currency} />
            </div>
        );
    }

    const summaryMonthName = new Date(summaryFilters.year, summaryFilters.month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
        <div className="clinic-dashboard">
            <div className="clinic-dashboard-header" style={{ marginBottom: '24px' }}>
                <div className="clinic-dashboard-header-text">
                    <h1>{t('clinicSidebar.cards.dashboard') || 'Dashboard'}</h1>
                    <p>{summaryMonthName}</p>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                    <DateFilter
                        mode="monthYear"
                        value={summaryFilters}
                        onChange={setSummaryFilters}
                    />
                </div>
            </div>

            {/* Daily Operations */}
            <div className="clinic-dashboard-landing-section">
                <h2 className="clinic-dashboard-section-heading">
                    <ClipboardList size={20} />
                    Daily Operations
                </h2>
                <div className="clinic-dashboard-landing-grid">
                    <LandingCard
                        title="Daily Operations"
                        description="Today's appointments, occupancy, and staff"
                        metric={loadingSummary ? '...' : summary.daily_appointments.toLocaleString()}
                        metricUnit="appointments today"
                        icon={CalendarDays}
                        onClick={() => navigateTo(VIEWS.daily)}
                        delay={0}
                    />
                    <LandingCard
                        title="Capacity & Utilization"
                        description="Room occupancy, vet workload, and peak hours"
                        metric={loadingSummary ? '...' : `${summary.capacity_utilization}%`}
                        metricUnit="vet utilization"
                        icon={Gauge}
                        onClick={() => navigateTo(VIEWS.capacity)}
                        delay={80}
                    />
                    <LandingCard
                        title="Veterinarian Workload"
                        description="Daily vet workload, appointments, and surgeries"
                        metric={loadingSummary ? '...' : summary.vet_workload_active.toLocaleString()}
                        metricUnit="active vets"
                        icon={Activity}
                        onClick={() => navigateTo(VIEWS.workload)}
                        delay={160}
                    />
                </div>
            </div>

            {/* Financial & Performance */}
            <div className="clinic-dashboard-landing-section">
                <h2 className="clinic-dashboard-section-heading">
                    <DollarSign size={20} />
                    Financial & Performance
                </h2>
                <div className="clinic-dashboard-landing-grid">
                    <LandingCard
                        title="Performance Overview"
                        description="Monthly clinic metrics and summaries"
                        metric={loadingSummary ? '...' : summary.performance_revenue.toLocaleString()}
                        metricUnit={`${currency} revenue`}
                        icon={Activity}
                        onClick={() => navigateTo(VIEWS.performance)}
                        delay={240}
                    />
                    <LandingCard
                        title="Financial Health"
                        description="Revenue, costs, and profitability trends"
                        metric={loadingSummary ? '...' : summary.financial_revenue.toLocaleString()}
                        metricUnit={`${currency} total revenue`}
                        icon={TrendingUp}
                        onClick={() => navigateTo(VIEWS.financial)}
                        delay={320}
                    />
                    <LandingCard
                        title="Growth & Trends"
                        description="Annual growth, patient trends, and diagnostics"
                        metric={loadingSummary ? '...' : summary.growth_new_clients.toLocaleString()}
                        metricUnit="new clients"
                        icon={GrowthIcon}
                        onClick={() => navigateTo(VIEWS.growth)}
                        delay={400}
                    />
                    <LandingCard
                        title="Veterinarian Productivity"
                        description="Revenue, admissions, procedures, and sales breakdown"
                        metric={loadingSummary ? '...' : summary.vet_productivity_avg.toLocaleString()}
                        metricUnit={`${currency} avg revenue`}
                        icon={TrendingUp}
                        onClick={() => navigateTo(VIEWS.productivity)}
                        delay={480}
                    />
                </div>
            </div>

            {/* Clinical Operations */}
            <div className="clinic-dashboard-landing-section">
                <h2 className="clinic-dashboard-section-heading">
                    <Stethoscope size={20} />
                    Clinical Operations
                </h2>
                <div className="clinic-dashboard-landing-grid">
                    <LandingCard
                        title="Clinical Activity"
                        description="Appointments, procedures, and clinical summary"
                        metric={loadingSummary ? '...' : summary.clinical_activity_appts.toLocaleString()}
                        metricUnit="total appointments"
                        icon={Activity}
                        onClick={() => navigateTo(VIEWS.clinical)}
                        delay={560}
                    />
                    <LandingCard
                        title="Inpatient Clinical Management"
                        description="Inpatient stays, species breakdown, and discharges"
                        metric={loadingSummary ? '...' : summary.inpatient_current.toLocaleString()}
                        metricUnit="current inpatients"
                        icon={Activity}
                        onClick={() => navigateTo(VIEWS.inpatient)}
                        delay={640}
                    />
                    <LandingCard
                        title="Outpatient Care"
                        description="Outpatient visits, follow-ups, and share by type"
                        metric={loadingSummary ? '...' : summary.outpatient_visits.toLocaleString()}
                        metricUnit="outpatient visits"
                        icon={CalendarDays}
                        onClick={() => navigateTo(VIEWS.outpatient)}
                        delay={720}
                    />
                    <LandingCard
                        title="Appointment Clinical Flow"
                        description="Monthly appointments, status, and provider breakdown"
                        metric={loadingSummary ? '...' : summary.appointment_completed.toLocaleString()}
                        metricUnit="completed this month"
                        icon={CalendarDays}
                        onClick={() => navigateTo(VIEWS.appointment)}
                        delay={800}
                    />
                </div>
            </div>
        </div>
    );
};

export default ClinicDashboard;
